import type { BookingStatus } from "@ai-admin/shared";
import { useTranslation } from "react-i18next";
import { focusRing } from "../../ui/index.js";
import { addDays, dayKey, dayStart, fmtDay, fmtTime } from "../cabinet/index.js";
import { minutesIntoDay, placeOverlaps } from "./grid.js";
import { isDead } from "./status.js";

/** Высота часа в сетке, px: 20-минутная запись вмещает строку, получасовая две. */
const HOUR = 72;
const GUTTER = "3.5rem";

/** Цвета мастеров в сетке (левая кромка записи и точка в фильтре); дальше шестого повторяются. */
export const masterColors = ["bg-sky-500", "bg-violet-500", "bg-emerald-500", "bg-orange-500", "bg-pink-500", "bg-teal-500"] as const;
/** Цвет мастера по порядковому номеру; неизвестный или неактивный мастер (нет в списке, index -1) серый. */
export const masterColor = (index: number): string => (index < 0 ? "bg-slate-400" : (masterColors[index % masterColors.length] ?? "bg-slate-400"));

const statusStyle: Record<BookingStatus, string> = {
  held: "border-slate-200 bg-slate-50 text-slate-700",
  pending: "border-amber-300 bg-amber-50 text-amber-950",
  confirmed: "border-slate-200 bg-white text-slate-900",
  completed: "border-slate-200 bg-slate-100 text-slate-700",
  no_show: "border-red-200 bg-red-50 text-red-900",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500 line-through",
  rejected: "border-slate-200 bg-slate-50 text-slate-500 line-through",
  expired: "border-slate-200 bg-slate-50 text-slate-500 line-through",
  released: "border-slate-200 bg-slate-50 text-slate-500 line-through",
};

export interface GridBooking {
  id: string;
  start: Date;
  end: Date;
  status: BookingStatus;
  masterIndex: number;
  /** Строки блока: клиент; услуги · мастер · «записал бот». */
  client: string;
  details: string;
}

export interface GridTimeOff {
  id: string;
  start: Date;
  end: Date;
  masterIndex: number;
  label: string;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Неделя сеткой для широкого экрана: колонки по дням, строки по часам, записи всех мастеров в одной
 * колонке дня (пересекающиеся встают рядом, мастер различается цветом кромки). Диапазон часов: 08–20,
 * расширяется под ранние и поздние записи. Текущее время отмечено линией.
 */
export function WeekGrid({
  week,
  day,
  today,
  tz,
  lang,
  bookings,
  timeOff,
  masterCount = 1,
  countByDay,
  onSelectDay,
  onOpenBooking,
}: {
  week: string;
  day: string;
  today: string;
  tz: string;
  lang: string;
  bookings: GridBooking[];
  timeOff: GridTimeOff[];
  /** Сколько мастеров показано: отгул каждого занимает свою дорожку колонки дня, при одном мастере всю колонку. */
  masterCount?: number;
  countByDay: Map<string, number>;
  onSelectDay: (day: string) => void;
  onOpenBooking: (id: string) => void;
}) {
  const { t } = useTranslation();
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const starts = new Map(days.map((k) => [k, dayStart(k, tz).getTime()]));
  const ends = new Map(days.map((k) => [k, dayStart(addDays(k, 1), tz).getTime()]));
  // Настенное время салона, а не миллисекунды от полуночи: в день перехода времени блоки не съезжают на час.
  const minutesInto = (k: string, at: Date) => minutesIntoDay(k, at, tz);

  let fromHour = 8;
  let toHour = 20;
  for (const b of bookings) {
    const k = dayKey(b.start, tz);
    if (!starts.has(k)) continue;
    fromHour = Math.min(fromHour, Math.floor(minutesInto(k, b.start) / 60));
    toHour = Math.max(toHour, Math.ceil(minutesInto(k, b.end) / 60));
  }
  fromHour = clamp(fromHour, 0, 23);
  toHour = clamp(toHour, fromHour + 1, 24);
  const hours = Array.from({ length: toHour - fromHour }, (_, i) => fromHour + i);
  const height = hours.length * HOUR;
  const top = (k: string, at: Date) => clamp((minutesInto(k, at) / 60 - fromHour) * HOUR, 0, height);
  const nowMinutes = today && starts.has(today) ? minutesInto(today, new Date()) : null;
  const nowTop = nowMinutes === null ? null : (nowMinutes / 60 - fromHour) * HOUR;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="grid border-b border-slate-200" style={{ gridTemplateColumns: `${GUTTER} repeat(7, minmax(0, 1fr))` }}>
        <div />
        {days.map((k) => {
          const n = countByDay.get(k) ?? 0;
          const active = k === day;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onSelectDay(k)}
              aria-pressed={active}
              className={`flex flex-col items-center border-l border-slate-100 py-2 text-xs transition hover:bg-slate-50 ${focusRing} ${
                active ? "bg-slate-900 text-white hover:bg-slate-800" : k === today ? "bg-slate-100" : ""
              }`}
            >
              <span className="opacity-70">{fmtDay(k, lang, { weekday: "short" })}</span>
              <span className="text-lg font-semibold leading-6">{Number(k.slice(8))}</span>
              <span className={`text-[11px] ${active ? "text-white/70" : "text-slate-500"}`}>{n > 0 ? n : " "}</span>
            </button>
          );
        })}
      </div>

      <div className="grid" style={{ gridTemplateColumns: `${GUTTER} repeat(7, minmax(0, 1fr))`, height }}>
        <div className="relative">
          {hours.map((h, i) => (
            <div key={h} className="absolute right-2 text-[11px] text-slate-400" style={{ top: i * HOUR - 7 }}>
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>
        {days.map((k) => {
          const dayBookings = bookings.filter((b) => dayKey(b.start, tz) === k);
          const dayOff = timeOff.filter((o) => o.start.getTime() < (ends.get(k) ?? 0) && o.end.getTime() > (starts.get(k) ?? 0));
          return (
            <div
              key={k}
              className={`relative border-l border-slate-100 ${k === day ? "bg-slate-50/60" : ""}`}
              style={{ backgroundImage: "repeating-linear-gradient(to bottom, #f1f5f9 0, #f1f5f9 1px, transparent 1px, transparent 72px)" }}
            >
              {dayOff.map((o) => {
                const y1 = top(k, o.start);
                const y2 = Math.max(top(k, o.end), y1 + 16);
                // Мастер вне списка (неактивный): во всю ширину бледнее. При многих мастерах дорожка узкая: только полоска цвета.
                const known = o.masterIndex >= 0 && o.masterIndex < masterCount;
                const lanes = known ? Math.max(1, masterCount) : 1;
                const lane = known ? o.masterIndex : 0;
                return (
                  <div
                    key={o.id}
                    className={`absolute overflow-hidden rounded-md px-1.5 py-0.5 text-[11px] leading-4 text-slate-600 ${known ? "" : "opacity-50"}`}
                    style={{
                      top: y1,
                      height: y2 - y1,
                      left: `calc(${(lane / lanes) * 100}% + 2px)`,
                      width: `calc(${(1 / lanes) * 100}% - 4px)`,
                      backgroundImage: "repeating-linear-gradient(135deg, #e2e8f0 0, #e2e8f0 4px, #f8fafc 4px, #f8fafc 10px)",
                    }}
                    title={o.label}
                  >
                    {lanes > 1 && <span className={`absolute inset-y-0 left-0 w-1 ${masterColor(o.masterIndex)}`} aria-hidden="true" />}
                    <span className={lanes > 3 ? "sr-only" : lanes > 1 ? "block pl-2" : ""}>{o.label}</span>
                  </div>
                );
              })}
              {/* Отменённые и отклонённые время не занимают: раскладываются отдельно под живыми, с живыми дорожки не делят. */}
              {[...placeOverlaps(dayBookings.filter((b) => isDead(b.status))), ...placeOverlaps(dayBookings.filter((b) => !isDead(b.status)))].map(
                ({ item: b, left, width }) => {
                  const y1 = top(k, b.start);
                  const y2 = Math.max(top(k, b.end), y1 + 20);
                  const timeLabel = `${fmtTime(b.start, tz, lang)}–${fmtTime(b.end, tz, lang)}`;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => onOpenBooking(b.id)}
                      title={`${timeLabel} ${b.client}`}
                      className={`absolute overflow-hidden rounded-md border text-left text-[11px] leading-4 shadow-sm transition hover:z-10 hover:shadow-md ${focusRing} ${statusStyle[b.status]} ${
                        isDead(b.status) ? "opacity-60" : ""
                      }`}
                      style={{ top: y1, height: y2 - y1, left: `calc(${left * 100}% + 2px)`, width: `calc(${width * 100}% - 4px)` }}
                    >
                      <span className={`absolute inset-y-0 left-0 w-1 ${masterColor(b.masterIndex)}`} aria-hidden="true" />
                      <span className="block truncate pl-2.5 pr-1 pt-0.5">
                        <span className="font-semibold">{timeLabel}</span> {b.client}
                      </span>
                      <span className="block truncate pl-2.5 pr-1">{b.details}</span>
                      <span className="block truncate pl-2.5 pr-1 opacity-80">{t(`cal.status.${b.status}`)}</span>
                    </button>
                  );
                },
              )}
              {k === today && nowTop !== null && nowTop >= 0 && nowTop <= height && (
                <div className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-red-500" style={{ top: nowTop }} aria-hidden="true" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
